import React, { useEffect } from 'react'
import { LayoutRowType, setUnresolvedDedupeEntities } from '@app-store'
import { HybridLayoutRowMapByRowType } from './HybridLayoutHybridRows'
import { HybridLayoutParserPropTypes } from './hybridLayout.types'
import { useFormContext, useWatch } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { RootState } from '../../utils/agent.store'
import { isEmpty } from 'lodash'
import {
  fieldKeyStagingMapping,
  reverseFieldKeyStagingMapping,
} from '../../utils/constants'
import { DedupeEntityNameType, DedupeFieldNameType } from '../../types/types'

const HybridLayoutParser = ({
  layoutData,
  recordId,
  formUniqueId,
  refetchTimer,
  refetchLogic,
  openAllAccordions,
  mode,
  task,
}: HybridLayoutParserPropTypes) => {
  const dispatch = useAppDispatch()
  const automationStagedDetails = useAppSelector(
    (state: RootState) => state?.workflowData.automationStagedDetails
  )
  const unresolvedDedupeEntitiesFromStore = useAppSelector(
    (state: RootState) => state.workflowData.unresolvedDedupeEntities
  )
  const { control } = useFormContext()
  const watchFields = Object.keys(
    fieldKeyStagingMapping
  ) as DedupeFieldNameType[]
  const values = useWatch({
    control,
    name: watchFields,
  })
  const [patientValue, facilityValue, providerValue] = values ?? []
  const dedupEntities = Object.keys(
    automationStagedDetails?.dedup_results ?? {}
  ) as DedupeEntityNameType[]
  const dedupeFieldNames: DedupeFieldNameType[] = dedupEntities
    .map((entity) => reverseFieldKeyStagingMapping[entity])
    .filter((value) => !!value)

  useEffect(() => {
    if (!automationStagedDetails) return
    if (!dedupeFieldNames.length) {
      if (unresolvedDedupeEntitiesFromStore.length !== 0) {
        dispatch(setUnresolvedDedupeEntities([]))
      }
      return
    }

    const entityValueMap: Record<DedupeFieldNameType, unknown | undefined> =
      Object.fromEntries(
        watchFields.map((key, index) => [key, values?.[index]])
      ) as Record<DedupeFieldNameType, unknown>

    const unresolvedEntities = dedupeFieldNames.filter((entityKey) =>
      isEmpty(entityValueMap[entityKey])
    )

    // only dispatch if something actually changed
    const hasChanged =
      unresolvedEntities.length !== unresolvedDedupeEntitiesFromStore.length ||
      unresolvedEntities.some(
        (entity, index) => entity !== unresolvedDedupeEntitiesFromStore[index]
      )

    if (hasChanged) {
      dispatch(setUnresolvedDedupeEntities(unresolvedEntities))
    }
  }, [
    dedupEntities,
    patientValue,
    facilityValue,
    providerValue,
    unresolvedDedupeEntitiesFromStore,
    watchFields,
    dispatch,
  ])

  return layoutData?.layout_data?.pages?.[0]?.rows?.map(
    (row: LayoutRowType, index: number) => (
      <React.Fragment key={row.rowId}>
        <HybridLayoutRowMapByRowType
          formUniqueId={formUniqueId}
          row={row}
          entityId={layoutData?.entity_id?.toString()}
          recordId={recordId?.toString()}
          index={index}
          refetchTimer={refetchTimer}
          refetchLogic={refetchLogic}
          openAllAccordions={openAllAccordions}
          mode={mode}
          task={task}
        />
      </React.Fragment>
    )
  )
}

export default HybridLayoutParser
